/**
 * Sentinel Certification Platform API client.
 *
 * Talks to the `sentinel-api` Supabase Edge Function which handles all
 * backend logic: program catalog, license validation, progress tracking,
 * capstone submission, certification issuance, seat management, renewals,
 * certificates, badges, admin metrics, and Wix Online Programs / Pricing
 * Plans / Members proxying.
 *
 * When the edge function is unavailable (network error, not deployed), the
 * client falls back to seeded data from `./certifications.ts` so the UI
 * remains functional during development.
 */

import {
  CERTIFICATION_PROGRAMS,
  AGENCY_ENTERPRISE_PLANS,
  DEMO_USER,
  DEMO_CERT_STATUSES,
  DEMO_SEAT_USERS,
  DEMO_ORGANIZATION,
  ADMIN_METRICS,
  getProgramById,
  type CertificationProgram,
  type PricingLicense,
  type UserMember,
  type CertificationStatusRecord,
} from "./certifications";

const API_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sentinel-api`;
const API_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY ?? "";

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T | null> {
  try {
    const res = await fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${API_KEY}`,
        ...options.headers,
      },
    });
    if (!res.ok) {
      console.warn(`[sentinel-api] ${path} returned ${res.status}`);
      return null;
    }
    return (await res.json()) as T;
  } catch (err) {
    console.warn(`[sentinel-api] ${path} failed`, err);
    return null;
  }
}

async function apiPost<T>(path: string, body: unknown): Promise<T | null> {
  return apiFetch<T>(path, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ─── Types matching the edge function responses ─────────────────────
export interface ProgramListResponse {
  programs: CertificationProgram[];
}

export interface ProgramDetailResponse {
  program: CertificationProgram;
  wixContent: unknown | null;
}

export interface LicenseListResponse {
  licenses: PricingLicense[];
}

export interface UserOverviewResponse {
  user: UserMember;
  progress: unknown[];
  certifications: CertificationStatusRecord[];
  renewals: unknown[];
  seats: unknown[];
  certificates: unknown[];
  badges: unknown[];
  orgLicenses: unknown[];
}

export interface OrgSeatsResponse {
  licenses: PricingLicense[];
  seats: (SeatAssignment & { sentinel_users?: { id: string; name: string; email: string; role: string } })[];
}

export interface SeatAssignment {
  id: string;
  license_id: string;
  user_id: string;
  assigned_by: string | null;
  status: "active" | "revoked" | "pending";
  assigned_at: string;
  revoked_at: string | null;
}

export interface EnrollResponse {
  enrolled: boolean;
  checkoutNeeded: boolean;
  checkoutUrl?: string;
  pricePlanId?: string;
  price?: number;
  currency?: string;
}

export interface CertificateRecord {
  id: string;
  user_id: string;
  program_id: string;
  certificate_url: string | null;
  issued_at: string;
  expires_at: string | null;
  verification_code: string;
}

export interface BadgeRecord {
  id: string;
  user_id: string;
  program_id: string;
  badge_image_url: string | null;
  issued_at: string;
  verification_code: string;
}

export interface AdminMetricsResponse {
  metrics: {
    total_users: number;
    active_certifications: number;
    active_licenses: number;
    seat_utilization_by_industry: Record<string, { assigned: number; total: number }>;
    renewal_rates: { total: number; completed: number; rate: number };
    generated_at: string;
  };
}

// ─── Programs ──────────────────────────────────────────────────────

export async function fetchPrograms(): Promise<CertificationProgram[]> {
  const res = await apiFetch<ProgramListResponse>("/programs");
  if (res?.programs?.length) return res.programs;
  return CERTIFICATION_PROGRAMS;
}

export async function fetchProgram(id: string): Promise<ProgramDetailResponse | null> {
  const res = await apiFetch<ProgramDetailResponse>(`/programs/${id}`);
  if (res?.program) return res;
  const program = getProgramById(id);
  return program ? { program, wixContent: null } : null;
}

export async function updateProgram(programId: string, updates: Record<string, unknown>): Promise<unknown> {
  return apiPost("/program/update", { program_id: programId, ...updates });
}

// ─── Users ──────────────────────────────────────────────────────────

export async function fetchUser(userId: string): Promise<{ user: UserMember } | null> {
  return apiFetch(`/user/${userId}`);
}

export async function createUser(userData: {
  auth_id?: string;
  name: string;
  email: string;
  role?: string;
  organization_id?: string;
}): Promise<unknown> {
  return apiPost("/user/create", userData);
}

export async function updateUser(userId: string, updates: Record<string, unknown>): Promise<unknown> {
  return apiPost("/user/update", { user_id: userId, ...updates });
}

const TEST_EMAIL_DOMAINS = ["testindustries.com", "e-industries.net"];

function isTestUser(userId: string): boolean {
  // Test accounts have known UUIDs from the seed migration.
  // For safety, also check if the email matches test domains.
  // The demo data fallback only applies to test accounts.
  return false; // Real users never get demo data; test accounts are seeded directly in the DB.
}

export async function fetchUserOverview(userId: string): Promise<UserOverviewResponse | null> {
  const res = await apiFetch<UserOverviewResponse>(`/user/${userId}/overview`);
  if (res?.user) return res;
  // No API response — return empty data for real users, not demo data.
  // Test accounts are seeded directly in the database and will get real data from the API.
  return {
    user: {
      id: userId,
      name: "",
      email: "",
      role: "student",
      active_licenses: [],
      completed_certifications: [],
      progress: {},
    } as UserMember,
    progress: [],
    certifications: [],
    renewals: [],
    seats: [],
    certificates: [],
    badges: [],
    orgLicenses: [],
  };
}

// ─── Organizations ──────────────────────────────────────────────────

export async function fetchOrg(orgId: string): Promise<unknown> {
  return apiFetch(`/org/${orgId}`);
}

export async function createOrg(orgData: { name: string; industry?: string; type: string }): Promise<unknown> {
  return apiPost("/org/create", orgData);
}

export async function updateOrg(orgId: string, updates: Record<string, unknown>): Promise<unknown> {
  return apiPost("/org/update", { organization_id: orgId, ...updates });
}

// ─── Licenses ───────────────────────────────────────────────────────

export async function fetchLicenses(): Promise<PricingLicense[]> {
  const res = await apiFetch<LicenseListResponse>("/licenses");
  if (res?.licenses?.length) return res.licenses;
  return AGENCY_ENTERPRISE_PLANS;
}

export async function fetchUserLicenses(userId: string): Promise<PricingLicense[]> {
  const res = await apiFetch<LicenseListResponse>(`/licenses/${userId}`);
  return res?.licenses ?? [];
}

export async function fetchOrgLicenses(orgId: string): Promise<PricingLicense[]> {
  const res = await apiFetch<LicenseListResponse>(`/licenses/org/${orgId}`);
  return res?.licenses ?? [];
}

export async function createLicense(licenseData: Record<string, unknown>): Promise<unknown> {
  return apiPost("/licenses/create", licenseData);
}

export async function updateLicense(licenseId: string, updates: Record<string, unknown>): Promise<unknown> {
  return apiPost("/licenses/update", { license_id: licenseId, ...updates });
}

// ─── Department Licenses ────────────────────────────────────────────

export interface DepartmentLicense {
  id: string;
  name: string;
  type: string;
  license_tier: string;
  seats: number;
  industry: string;
  status: string;
  price: number;
  currency: string;
  start_date: string;
  end_date: string;
  organization_id: string;
  department_id: string | null;
}

export async function fetchDepartmentLicenses(orgId: string, departmentId?: string): Promise<DepartmentLicense[]> {
  const path = departmentId
    ? `/licenses/org/${orgId}/dept/${departmentId}`
    : `/licenses/org/${orgId}`;
  const res = await apiFetch<{ licenses: DepartmentLicense[] }>(path);
  return res?.licenses ?? [];
}

export async function assignLicenseToDepartment(licenseId: string, departmentId: string): Promise<unknown> {
  return apiPost("/licenses/assign-department", { license_id: licenseId, department_id: departmentId });
}

// ─── Departments ────────────────────────────────────────────────────

export interface DepartmentRecord {
  id: string;
  organization_id: string;
  name: string;
  budget: number | null;
  created_by: string | null;
  admin_user_id: string | null;
  created_at: string;
}

export async function fetchDepartments(orgId: string): Promise<DepartmentRecord[]> {
  const res = await apiFetch<{ departments: DepartmentRecord[] }>(`/org/${orgId}/departments`);
  return res?.departments ?? [];
}

export async function createDepartment(orgId: string, name: string, budget?: number, adminUserId?: string): Promise<unknown> {
  return apiPost("/departments/create", { organization_id: orgId, name, budget, admin_user_id: adminUserId });
}

export async function updateDepartment(deptId: string, updates: Record<string, unknown>): Promise<unknown> {
  return apiPost("/departments/update", { department_id: deptId, ...updates });
}

// ─── Seats ──────────────────────────────────────────────────────────

export async function fetchOrgSeats(orgId: string): Promise<OrgSeatsResponse> {
  const res = await apiFetch<OrgSeatsResponse>(`/organization/${orgId}/seats`);
  if (res?.licenses) return res;
  return { licenses: [], seats: [] };
}

export async function fetchLicenseSeats(licenseId: string): Promise<unknown> {
  return apiFetch(`/seats/${licenseId}`);
}

export async function assignSeat(licenseId: string, userId: string, assignedBy?: string): Promise<unknown> {
  return apiPost("/seats/assign", { license_id: licenseId, user_id: userId, assigned_by: assignedBy });
}

export async function revokeSeat(licenseId: string, userId: string): Promise<unknown> {
  return apiPost("/seats/revoke", { license_id: licenseId, user_id: userId });
}

export async function transferSeat(licenseId: string, fromUserId: string, toUserId: string, assignedBy?: string): Promise<unknown> {
  return apiPost("/seats/transfer", { license_id: licenseId, from_user_id: fromUserId, to_user_id: toUserId, assigned_by: assignedBy });
}

// ─── Progress ───────────────────────────────────────────────────────

export async function fetchProgress(userId: string, programId: string): Promise<unknown> {
  return apiFetch(`/progress/${userId}/${programId}`);
}

export async function updateProgress(
  userId: string,
  programId: string,
  moduleId: string,
  status: "not_started" | "in_progress" | "completed",
): Promise<unknown> {
  return apiPost("/progress/update", { user_id: userId, program_id: programId, module_id: moduleId, status });
}

export async function submitQuizScore(
  userId: string,
  programId: string,
  moduleId: string,
  quizScore: number,
  quizPassed?: boolean,
): Promise<unknown> {
  return apiPost("/progress/quiz-score", { user_id: userId, program_id: programId, module_id: moduleId, quiz_score: quizScore, quiz_passed: quizPassed });
}

// ─── Capstones ──────────────────────────────────────────────────────

export async function fetchCapstone(userId: string, programId: string): Promise<unknown> {
  return apiFetch(`/capstone/${userId}/${programId}`);
}

export async function submitCapstone(
  userId: string,
  programId: string,
  capstoneId: string,
  score: number,
  status: "completed" | "failed" = "completed",
  submissionUrl?: string,
): Promise<unknown> {
  return apiPost("/certification/capstone-status", { user_id: userId, program_id: programId, capstone_id: capstoneId, score, status, submission_url: submissionUrl });
}

// ─── Certification Status ────────────────────────────────────────────

export async function fetchUserCertifications(userId: string): Promise<unknown> {
  return apiFetch(`/certification/${userId}`);
}

export async function updateCertification(userId: string, programId: string, updates: Record<string, unknown>): Promise<unknown> {
  return apiPost("/certification/update", { user_id: userId, program_id: programId, ...updates });
}

export async function issueCertification(userId: string, programId: string): Promise<unknown> {
  return apiPost("/certification/issue", { user_id: userId, program_id: programId });
}

// ─── Certificates ───────────────────────────────────────────────────

export async function fetchUserCertificates(userId: string): Promise<CertificateRecord[]> {
  const res = await apiFetch<{ certificates: CertificateRecord[] }>(`/certificates/${userId}`);
  return res?.certificates ?? [];
}

export async function createCertificate(userId: string, programId: string, certificateUrl?: string, expiresAt?: string): Promise<unknown> {
  return apiPost("/certificates/create", { user_id: userId, program_id: programId, certificate_url: certificateUrl, expires_at: expiresAt });
}

// ─── Badges ─────────────────────────────────────────────────────────

export async function fetchUserBadges(userId: string): Promise<BadgeRecord[]> {
  const res = await apiFetch<{ badges: BadgeRecord[] }>(`/badges/${userId}`);
  return res?.badges ?? [];
}

export async function createBadge(userId: string, programId: string, badgeImageUrl?: string): Promise<unknown> {
  return apiPost("/badges/create", { user_id: userId, program_id: programId, badge_image_url: badgeImageUrl });
}

// ─── Renewals ───────────────────────────────────────────────────────

export async function fetchUserRenewals(userId: string): Promise<unknown> {
  return apiFetch(`/certification/renewals/${userId}`);
}

export async function renewCertification(
  userId: string,
  programId: string,
  examScore: number,
  cecModulesCompleted: number,
  renewalProgramId?: string,
): Promise<unknown> {
  return apiPost("/certification/renew", { user_id: userId, program_id: programId, exam_score: examScore, cec_modules_completed: cecModulesCompleted, renewal_program_id: renewalProgramId });
}

// ─── Enrollment ──────────────────────────────────────────────────────

export async function enroll(userId: string, programId: string): Promise<EnrollResponse | null> {
  return apiPost<EnrollResponse>("/enroll", { user_id: userId, program_id: programId });
}

// ─── Program Content ───────────────────────────────────────────────

export interface ProgramContentSection {
  heading: string;
  body: string;
  callout?: string;
}

export interface ProgramContentRecord {
  id: string;
  program_id: string;
  module_id: string;
  lesson_id: string;
  title: string;
  content: string;
  sections: ProgramContentSection[];
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
}

export async function fetchProgramContent(programId: string): Promise<ProgramContentRecord[]> {
  const res = await apiFetch<{ content: ProgramContentRecord[] }>(`/program-content/${programId}`);
  return res?.content ?? [];
}

export async function fetchLessonContent(programId: string, moduleId: string, lessonId: string): Promise<ProgramContentRecord | null> {
  const res = await apiFetch<{ content: ProgramContentRecord }>(`/program-content/${programId}/${moduleId}/${lessonId}`);
  return res?.content ?? null;
}

export async function saveProgramContent(
  programId: string,
  moduleId: string,
  lessonId: string,
  title: string,
  content: string,
  sections: ProgramContentSection[],
): Promise<unknown> {
  return apiPost("/program-content/save", {
    program_id: programId,
    module_id: moduleId,
    lesson_id: lessonId,
    title,
    content,
    sections,
  });
}

export async function deleteProgramContent(programId: string, moduleId: string, lessonId: string): Promise<unknown> {
  return apiPost("/program-content/delete", { program_id: programId, module_id: moduleId, lesson_id: lessonId });
}

// ─── Admin Metrics ──────────────────────────────────────────────────

export async function fetchAdminMetrics(): Promise<AdminMetricsResponse | null> {
  return apiFetch<AdminMetricsResponse>("/admin/metrics");
}

/** Admin metrics — tries API first, falls back to seeded data. */
export async function getAdminMetricsAsync(): Promise<AdminMetricsResponse["metrics"]> {
  const res = await fetchAdminMetrics();
  if (res?.metrics) return res.metrics;
  return ADMIN_METRICS as unknown as AdminMetricsResponse["metrics"];
}

/** Synchronous fallback for components that need immediate data. */
export function getAdminMetrics() {
  return ADMIN_METRICS;
}

export function getDemoSeatUsers() {
  return DEMO_SEAT_USERS;
}

export function getDemoOrganization() {
  return DEMO_ORGANIZATION;
}
